/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import {
  FiPlus,
  FiEye,
  FiEdit2,
  FiX,
  FiUploadCloud,
  FiTrash2, FiPackage
} from "react-icons/fi";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import PageContainer from "../components/PageContainer";
import ResponsiveTable from "../components/ResponsiveTable";
import { useUI } from "../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";


const createEmptyCategory = () => ({
  id: null,
  subCategoryId: null,
  parentCategoryId: null,
  name: "",
  imageUrl: "",
  imageFile: null,
  active: true,
  position: "رئيسي",
  parent: "",
  description: "",
});


const forceSwalCleanup = () => {
  document.querySelectorAll('.swal2-container, .swal2-backdrop-show').forEach(el => {
    el.remove();
  });

  document.body.style.overflow = "auto";
};
const configureSwalTop = () => {
  return {
    customClass: { container: 'swal2-container-top' },

    didOpen: () => {
      if (!document.getElementById('swal-top-style')) {
        const style = document.createElement('style');
        style.id = 'swal-top-style';
        style.textContent = `
          .swal2-container-top {
            position: fixed !important;
            top: 20px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            z-index: 10000 !important;
          }

          /* ✅ إزالة السواد الخلفي فقط */
          .swal2-backdrop-show {
            background: transparent !important;
          }

          /* ✅ إطار + ظل + خلفية واضحة */
          .swal2-popup {
            background: #ffffff !important;
            border: 1px solid black !important;   /* بنفسجي أنيق */
            border-radius: 16px !important;
            box-shadow: 0 20px 40px rgba(0,0,0,0.25) !important;
          }
        `;
        document.head.appendChild(style);
      }
    },

    willClose: () => {
      document.body.style.overflow = "auto";
    },

    didClose: () => {
      document.body.style.overflow = "auto";
    },
  };
};
export default function AdminCategories() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(
        `${baseUrl}/TenantCategory/GetCategories?TenantId=${tenantId}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      if (Array.isArray(data)) {
        setRawCategories(data); // ← نخزن البيانات الأصلية كاملة

        const mapped = mapCategoriesFromApi(data);
        setCategories(mapped);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };
  const baseUrl =
    process.env.NODE_ENV === "development"
      ? "/api"
      : (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "-1");
  console.log(tenantId);
  const [rawCategories, setRawCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showView, setShowView] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [newCategory, setNewCategory] = useState(createEmptyCategory());
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  // Fetch categories from API on component mount
  useEffect(() => {
    if (tenantId && tenantId !== -1) {
      fetchCategories();
      fetchProducts();
    }
  }, [tenantId]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `${baseUrl}/GetItems?tenantId=${tenantId}&itemId=-1`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );
      if (!res.ok) return;
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching products for divisions:", err);
      setProducts([]);
    }
  };

  const getItemCategoryId = (item) =>
    Number(
      item?.category?.id ??
        item?.category?.categoryId ??
        item?.categoryId ??
        item?.CategoryId ??
        0
    );

  const getItemSubCategoryId = (item) =>
    Number(
      item?.subCategory?.subCategoryId ??
        item?.subCategoryId ??
        item?.SubCategoryId ??
        0
    );

  const getCategoryItems = (categoryId) =>
    products.filter((item) => getItemCategoryId(item) === Number(categoryId));

  const getSubCategoryItems = (categoryId, subCategoryId) =>
    products.filter(
      (item) =>
        getItemCategoryId(item) === Number(categoryId) &&
        getItemSubCategoryId(item) === Number(subCategoryId)
    );

  const [products, setProducts] = useState([]);

  // Auto-open Add Modal if coming from AllProducts
  useEffect(() => {
    if (location.state?.openAdd && perms.addDivisions) {
      setShowAdd(true);
      // Optional: Clear state so it doesn't re-open on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, perms.addDivisions]);

  const buildImageUrl = (imageUrl) => {
    if (!imageUrl || imageUrl === "" || imageUrl === "null" || imageUrl === "undefined") {
      return "https://placehold.co/400x400.png?text=No+Image";
    }

    if (typeof imageUrl !== "string") {
      return "https://placehold.co/400x400.png?text=No+Image";
    }

    // معاينة محلية بعد اختيار ملف
    if (imageUrl.startsWith("blob:") || imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      return imageUrl;
    }

    // أساس الصور بدون /api — مثل المتجر: /uploads/{tenantId}/categories/{file}
    const apiBase =
      process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api";
    const imageBase = String(apiBase).replace(/\/+$/, "").replace(/\/api$/i, "");

    let cleanPath = imageUrl.startsWith("/") ? imageUrl.slice(1) : imageUrl;

    if (cleanPath.startsWith("uploads/")) {
      return `${imageBase}/${cleanPath}`;
    }

    const fileName = cleanPath.split("/").pop();
    return `${imageBase}/uploads/${tenantId}/categories/${fileName}`;
  };





  function mapCategoriesFromApi(data) {
    const rows = [];

    data.forEach(cat => {
      const rawImage = cat.imageUrl || cat.ImageUrl || cat.image || cat.Image || "";
      // 1. إضافة القسم الرئيسي
      rows.push({
        id: cat.categoryId, // المعرف الأساسي للرئيسي
        subCategoryId: null, // لا يوجد ID فرعي هنا
        name: cat.categoryName,
        description: cat.description,
        active: cat.isActive === 1 || cat.isActive === true,
        position: "رئيسي",
        parent: "",
        productCount: 0,
        imageUrl: buildImageUrl(rawImage),
      });

      // 2. إضافة الأقسام الفرعية (إذا وجدت)
      if (Array.isArray(cat.subCategories)) {
        cat.subCategories.forEach(sub => {
          const subRawImage =
            sub.imageUrl || sub.ImageUrl || sub.image || sub.Image || rawImage;
          rows.push({
            id: cat.categoryId, // المعرف الرئيسي للأب (مهم جداً للـ Edit)
            subCategoryId: sub.subCategoryId, // المعرف الفرعي الفعلي
            name: sub.subCategoryName,
            description: sub.description,
            active: cat.isActive === 1 || cat.isActive === true,
            position: "فرعي",
            parent: cat.categoryName,
            parentCategoryId: cat.categoryId,
            productCount: sub.productsCount || 0,
            imageUrl: buildImageUrl(subRawImage),
          });
        });
      }
    });

    return rows;
  }








  // Handle ESC key to close zoom modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && zoomedImage) {
        setZoomedImage(null);
      }
    };

    if (zoomedImage) {
      window.addEventListener('keydown', handleEscKey);
      return () => {
        window.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [zoomedImage]);



  // Function to fetch a specific category by ID
  const fetchCategoryById = async (categoryId) => {
    try {
      if (categories.find(c => c.subCategoryId === categoryId)) {
        const sub = categories.find(c => c.subCategoryId === categoryId);
        categoryId = sub.parentCategoryId;
      }


      const token = localStorage.getItem("authToken");

      // Use proxy in development, full URL in production


      // Use GetCategories and filter by categoryId
      const res = await fetch(
        `${baseUrl}/TenantCategory/GetCategories?TenantId=${tenantId}`,
        {
          method: "GET",
          headers: {
            "Accept": "*/*",
            "Authorization": token ? `Bearer ${token}` : "",
          },
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      // Try to parse response
      let data = [];
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse JSON response:", parseError);
          console.error("Response text:", text);
          throw new Error("Invalid response format");
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            throw new Error("Invalid response format");
          }
        }
      }

      // Filter to find the specific category
      if (Array.isArray(data)) {
        const category = data.find(
          (item) =>
            (item.categoryId || item.id || item.CategoryId) === categoryId
        );
        if (category) {
          console.log("✅ GetCategories Response (filtered):", category);
          return category;
        }
      }

      console.log("⚠️ Category not found in GetCategories response");
      return null;
    } catch (err) {
      console.error("GetCategories error:", err);

      // Handle specific error cases
      let errorMessage = err.message || String(err);
      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        errorMessage = isArabic
          ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت"
          : "Connection failed. Please check your internet connection";
      } else if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
        errorMessage = isArabic
          ? "غير مصرح. يرجى تسجيل الدخول مرة أخرى"
          : "Unauthorized. Please login again";
      } else if (errorMessage.includes("404")) {
        errorMessage = isArabic
          ? "القسم غير موجود"
          : "Category not found";
      } else if (errorMessage.includes("500")) {
        errorMessage = isArabic
          ? "خطأ في الخادم. يرجى المحاولة لاحقاً"
          : "Server error. Please try again later";
      }

      throw new Error(errorMessage);
    }
  };
  const fetchFullCategory = async (categoryId) => {
    const token = localStorage.getItem("authToken");
    const res = await fetch(`${baseUrl}/TenantCategory/GetCategories?TenantId=${tenantId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    return data.find(c => c.categoryId === categoryId);
  };
  const handleAdd = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const cat = newCategory;
      const formData = new FormData();
      const today = new Date().toISOString().split("T")[0];

      if (cat.position === "رئيسي") {
        formData.append("CategoryId", "0");
        formData.append("CategoryName", cat.name.trim());
        formData.append("Description", cat.description || "");
        formData.append("subCategories", JSON.stringify([]));
      } else {
        // ✅ جلب بيانات الأب كاملة لضمان عدم ضياع الفروع الأخرى
        const fullParent = await fetchFullCategory(cat.parentCategoryId);
        formData.append("CategoryId", fullParent.categoryId);
        formData.append("CategoryName", fullParent.categoryName);
        formData.append("Description", fullParent.description || "");

        // دمج القديم مع الجديد
        const existingSubs = fullParent.subCategories.map(s => ({
          SubCategoryId: s.subCategoryId,
          SubCategoryName: s.subCategoryName,
          Description: s.description
        }));

        existingSubs.push({
          SubCategoryId: 0, // 0 للإضافة الجديدة
          SubCategoryName: cat.name.trim(),
          Description: cat.description || ""
        });
        formData.append("subCategories", JSON.stringify(existingSubs));
      }

      formData.append("IsActive", cat.active ? 1 : 0);
      formData.append("CreatedAt", today);
      if (cat.imageFile) formData.append("Image", cat.imageFile);

      const res = await fetch(`${baseUrl}/EditTenantCategory?TenantId=${tenantId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error(await res.text());
      await fetchCategories();
      setShowAdd(false);
      setNewCategory(createEmptyCategory());
      if (location.state?.returnTo) {
        navigate(location.state.returnTo);
      }
    } catch (err) {
      Swal.fire({ icon: "error", text: err.message, ...configureSwalTop() });
    } finally { setLoading(false); }
  };

  const getProductsForModal = (categoryId, subCategoryId = null) => {
    if (subCategoryId) {
      return getSubCategoryItems(categoryId, subCategoryId);
    }
    return getCategoryItems(categoryId);
  };

  const buildProductImageUrl = (imagePath) => {
    if (!imagePath || imagePath === "" || imagePath === "null") {
      return "https://placehold.co/80x80/e0e0e0/666?text=Product";
    }
    if (typeof imagePath !== "string") {
      return "https://placehold.co/80x80/e0e0e0/666?text=Product";
    }
    if (imagePath.startsWith("blob:") || imagePath.startsWith("http")) {
      return imagePath;
    }
    const apiBase =
      process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api";
    const imageBase = String(apiBase).replace(/\/+$/, "").replace(/\/api$/i, "");
    let cleanPath = imagePath.startsWith("/") ? imagePath.slice(1) : imagePath;
    if (cleanPath.startsWith("uploads/")) {
      return `${imageBase}/${cleanPath}`;
    }
    return `${imageBase}/uploads/${tenantId}/items/${cleanPath.split("/").pop()}`;
  };

  const getProductStockQty = (item) => {
    const variants = item?.variants || item?.Variants || [];
    if (!Array.isArray(variants) || variants.length === 0) return 0;
    return variants.reduce((sum, v) => {
      const sizes = v.sizes || v.Sizes;
      if (Array.isArray(sizes) && sizes.length > 0) {
        return (
          sum +
          sizes.reduce(
            (sSum, sz) => sSum + Number(sz.stockQty || sz.StockQty || sz.quantity || 0),
            0
          )
        );
      }
      return sum + Number(v.stockQty || v.StockQty || v.quantity || v.Quantity || 0);
    }, 0);
  };

  const getProductPrice = (item) => {
    const variants = item?.variants || item?.Variants || [];
    if (!Array.isArray(variants) || variants.length === 0) return 0;
    const prices = variants
      .map((v) => Number(v.price || v.Price || 0))
      .filter((n) => Number.isFinite(n) && n > 0);
    return prices.length ? Math.min(...prices) : 0;
  };

  const subCategoryFilter = (list, categoryId, subCategoryId = null) => {
    if (subCategoryId) {
      return list.filter(
        (item) =>
          getItemCategoryId(item) === Number(categoryId) &&
          getItemSubCategoryId(item) === Number(subCategoryId)
      );
    }
    return list.filter(
      (item) => getItemCategoryId(item) === Number(categoryId)
    );
  };

  const handleViewProducts = async (row) => {
    let source = products;
    if (!source.length) {
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(
          `${baseUrl}/GetItems?tenantId=${tenantId}&itemId=-1`,
          {
            method: "GET",
            headers: {
              Accept: "*/*",
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );
        if (res.ok) {
          const data = await res.json();
          source = Array.isArray(data) ? data : [];
          setProducts(source);
        }
      } catch (err) {
        console.error("Error fetching products for divisions:", err);
      }
    }

    const list = subCategoryFilter(source, row.id, row.subCategoryId);
    setSelectedProducts(list);
    setShowProductsModal(true);
  };
  const handleEditSave = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const cat = showEdit; // القسم المختار للتعديل
      const formData = new FormData();

      // جلب بيانات الأب (سواء كان المختار رئيسي أو فرعي)
      const fullParent = await fetchFullCategory(cat.id);

      formData.append("CategoryId", fullParent.categoryId);
      formData.append("IsActive", cat.active ? 1 : 0);
      formData.append("CreatedAt", new Date().toISOString().split("T")[0]);
      if (cat.imageFile) formData.append("Image", cat.imageFile);

      if (cat.position === "رئيسي") {
        formData.append("CategoryName", cat.name.trim());
        formData.append("Description", cat.description || "");
        // إرسال الفروع كما هي دون تغيير
        const subs = fullParent.subCategories.map(s => ({
          SubCategoryId: s.subCategoryId, SubCategoryName: s.subCategoryName, Description: s.description
        }));
        formData.append("subCategories", JSON.stringify(subs));
      } else {
        // تعديل فرعي: نحافظ على اسم الأب ونحدث الفرع المعين في المصفوفة
        formData.append("CategoryName", fullParent.categoryName);
        formData.append("Description", fullParent.description || "");

        const updatedSubs = fullParent.subCategories.map(s =>
          s.subCategoryId === cat.subCategoryId
            ? { SubCategoryId: s.subCategoryId, SubCategoryName: cat.name.trim(), Description: cat.description || "" }
            : { SubCategoryId: s.subCategoryId, SubCategoryName: s.subCategoryName, Description: s.description }
        );
        formData.append("subCategories", JSON.stringify(updatedSubs));
      }

      const res = await fetch(`${baseUrl}/EditTenantCategory?TenantId=${tenantId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error(await res.text());
      await fetchCategories();
      setShowEdit(null);
      if (location.state?.returnTo) {
        navigate(location.state.returnTo);
      }
    } catch (err) {
      Swal.fire({ icon: "error", text: err.message, ...configureSwalTop() });
    } finally { setLoading(false); }
  };

  const handleDelete = async (row) => {
    if (!perms.deleteDivisions) {
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: isArabic ? "هل أنت متأكد؟" : "Are you sure?",
      text: isArabic
        ? `هل تريد حذف القسم "${row.name}"؟`
        : `Do you want to delete category "${row.name}"?`,
      showCancelButton: true,
      confirmButtonColor: colors.red,
      cancelButtonColor: colors.mainColor,
      confirmButtonText: isArabic ? "حذف" : "Delete",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
      background: isDark ? "#1E293B" : "#fff",
      color: isDark ? "#fff" : "#000",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");
      let res;

      if (row.position === "رئيسي") {
        res = await fetch(`${baseUrl}/TenantCategory/DeleteCategory?TenantId=${tenantId}&CategoryId=${row.id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });
      } else {
        const fullParent = await fetchFullCategory(row.id);
        const formData = new FormData();
        formData.append("CategoryId", fullParent.categoryId);
        formData.append("CategoryName", fullParent.categoryName);
        formData.append("IsActive", fullParent.isActive ? 1 : 0);

        const remainingSubs = fullParent.subCategories
          .filter(s => s.subCategoryId !== row.subCategoryId)
          .map(s => ({
            SubCategoryId: s.subCategoryId,
            SubCategoryName: s.subCategoryName,
            Description: s.description
          }));

        formData.append("subCategories", JSON.stringify(remainingSubs));

        res = await fetch(`${baseUrl}/EditTenantCategory?TenantId=${tenantId}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      }

      const text = await res.text();
      let data = {};
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          data = { message: text };
        }
      }

      if (!res.ok || data.success === false) {
        Swal.fire({
          icon: "error",
          title: isArabic ? "فشل الحذف ❌" : "Delete Failed ❌",
          text: data.message || data.error || `HTTP ${res.status}: ${res.statusText}` || (isArabic ? "فشل حذف القسم" : "Failed to delete category"),
          confirmButtonColor: colors.mainColor,
          background: isDark ? "#1E293B" : "#fff",
          color: isDark ? "#fff" : "#000",
          ...configureSwalTop(),
        });
        return;
      }

      await fetchCategories();

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحذف! ✅" : "Deleted! ✅",
        timer: 1500,
        showConfirmButton: false,
        text: data.message || (isArabic ? "تم حذف القسم بنجاح" : "Category deleted successfully"),
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
        ...configureSwalTop(),
      });
    } catch (err) {
      console.error("DeleteCategory error:", err);

      let errorMessage = err.message || String(err);
      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        errorMessage = isArabic
          ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت"
          : "Connection failed. Please check your internet connection";
      }

      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الحذف ❌" : "Delete Failed ❌",
        text: errorMessage,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
        ...configureSwalTop(),
      });
    }
  };

  // Note: Search is now handled by ResponsiveTable component
  const filtered = categories;
  useEffect(() => {
    if (!showAdd && !showEdit && !showView && !zoomedImage) {
      document.body.style.overflow = "auto";
    } else {
      document.body.style.overflow = "hidden";
    }
  }, [showAdd, showEdit, showView, zoomedImage]);


  if (!perms.viewDivisions) {
    return <NoAccess />;
  }

  return (
    <PageContainer title={isArabic ? "تقسيمات المتجر" : "Store Categories"}>
      {/* ===== البحث + إضافة ===== */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        {perms.addDivisions && (
          <button
            onClick={() => {
              setShowEdit(null);
              setShowView(null);
              setZoomedImage(null);

              // إعادة تهيئة كاملة
              const fresh = createEmptyCategory();
              setNewCategory(fresh);

              setTimeout(() => setShowAdd(true), 0); // مهم لمنع React من إعادة استخدام المرجع
            }}

            className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-medium shadow-md active:scale-[1.02] transition-transform"
            style={{ background: colors.mainColor }}
          >
            <FiPlus /> {isArabic ? "إضافة قسم جديد" : "Add Category"}
          </button>
        )}
      </div>

      {/* ===== الجدول ===== */}
      <div className="w-full">
        <ResponsiveTable
          columns={[
            { header: isArabic ? "اسم القسم" : "Category Name", sortKey: "name", align: "center" },
            { header: isArabic ? "صورة القسم" : "Category Image", align: "center" },
            { header: isArabic ? "حالة التفعيل" : "Activation Status", sortKey: "active", align: "center" },
            { header: isArabic ? "مكانة القسم" : "Category Position", sortKey: "position", align: "center" },
            { header: isArabic ? "اسم القسم الرئيسي" : "Parent Category", sortKey: "parent", align: "center" },
            { header: isArabic ? "عدد المنتجات التابعة لهذا القسم" : "Number of Products", sortKey: "productCount", align: "center" },
            { header: isArabic ? "الإجراءات" : "Actions", align: "center" },
          ]}
          data={filtered}
          searchable={true}
          sortable={true}
          filterOptions={{
            field: "active",
            options: [
              { value: "", label: isArabic ? "الكل" : "All" },
              { value: "true", label: isArabic ? "نشط" : "Active" },
              { value: "false", label: isArabic ? "غير نشط" : "Inactive" },
            ],
          }}
          renderRow={(c) => (
            <>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                <span className="font-medium text-sm sm:text-base whitespace-nowrap">{c.name || "—"}</span>
              </td>
              <td className="py-3 px-4 text-center">
                <div
                  className="flex items-center justify-center cursor-pointer group relative"
                  onClick={() => setZoomedImage(c.imageUrl)}
                >
                  <img
                    src={c.imageUrl}
                    alt={c.name}
                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0 transition-all duration-300 group-active:scale-125 group-active:shadow-lg"
                    style={{ border: `2px solid ${colors.mainColor}20` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-active:bg-black/10 rounded-lg transition-all duration-300 opacity-0 group-active:opacity-100">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4 text-center">
                <span
                  className={`px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap ${c.active ? "text-green-400 bg-green-500/10" : "text-red-400 bg-red-500/10"
                    }`}
                >
                  {c.active ? (isArabic ? "نشط" : "Active") : isArabic ? "غير نشط" : "Inactive"}
                </span>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                <span className="text-sm sm:text-base whitespace-nowrap">
                  {c.position === "رئيسي" ? (isArabic ? "رئيسي" : "Main") : (isArabic ? "فرعي" : "Sub")}
                </span>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                <span className="text-sm sm:text-base whitespace-nowrap">
                  {c.position === "فرعي" && c.parent ? c.parent : "—"}
                </span>
              </td>
              <td className="py-3 px-4 text-center">
                <span className="font-semibold text-sm sm:text-base whitespace-nowrap" style={{ color: colors.gold }}>
                  {
                    c.position === "رئيسي"
                      ? getCategoryItems(c.id).length
                      : getSubCategoryItems(c.id, c.subCategoryId).length
                  }                </span>
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex justify-center items-center gap-2 flex-nowrap whitespace-nowrap">
                  <button
                    onClick={async () => {
                      try {
                        const categoryData = await fetchCategoryById(c.id);
                        if (categoryData) {
                          const rawImage =
                            categoryData.imageUrl ||
                            categoryData.ImageUrl ||
                            categoryData.image ||
                            categoryData.Image ||
                            "";
                          setShowView({
                            ...c,
                            name: categoryData.categoryName || c.name,
                            description: categoryData.description || c.description,
                            active: categoryData.isActive === true || categoryData.isActive === 1,
                            imageUrl: rawImage ? buildImageUrl(rawImage) : c.imageUrl,
                          });
                        } else {
                          setShowView(c);
                        }
                      } catch (err) {
                        setShowView(c);
                      }
                    }}
                    className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm active:shadow-md flex-shrink-0"
                    style={{ background: colors.mainColor }}
                    title={isArabic ? "عرض" : "View"}
                  >
                    <FiEye size={16} />
                  </button>
                  <button
                    onClick={() => handleViewProducts(c)}
                    className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm active:shadow-md flex-shrink-0"
                    style={{ background: colors.mainColor }}
                    title={isArabic ? "عرض المنتجات" : "View Products"}
                  >
                    <FiPackage size={16} />
                  </button>
                  {perms.editDivisions && (
                    <button
                      onClick={async () => {
                        try {
                          const categoryData = await fetchCategoryById(c.id);
                          if (categoryData) {
                            const rawImage =
                              categoryData.imageUrl ||
                              categoryData.ImageUrl ||
                              categoryData.image ||
                              categoryData.Image ||
                              "";
                            setShowEdit({
                              ...c,
                              name: c.position === "فرعي" ? c.name : categoryData.categoryName,
                              description: c.position === "فرعي" ? c.description : categoryData.description,
                              active: categoryData.isActive === true || categoryData.isActive === 1,
                              parentCategoryId: c.parentCategoryId || categoryData.categoryId,
                              subCategoryId: c.subCategoryId || null,
                              imageUrl: rawImage ? buildImageUrl(rawImage) : c.imageUrl,
                              imageFile: null,
                            });

                          } else {
                            setShowEdit(c);
                          }
                        } catch (err) {
                          setShowEdit(c);
                        }
                      }}
                      className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm active:shadow-md flex-shrink-0"
                      style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                      title={isArabic ? "تعديل" : "Edit"}
                    >
                      <FiEdit2 size={16} />
                    </button>
                  )}
                  {perms.deleteDivisions && (
                    <button
                      onClick={() => handleDelete(c)}
                      className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm active:shadow-md flex-shrink-0"
                      style={{ background: colors.red }}
                      title={isArabic ? "حذف" : "Delete"}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              </td>
            </>
          )}
          renderCard={(c) => (
            <div className="space-y-3">
              <div className="flex gap-4 items-start">
                <div
                  className="relative cursor-pointer group flex-shrink-0"
                  onClick={() => setZoomedImage(c.imageUrl)}
                >
                  <img
                    src={c.imageUrl}
                    alt={c.name}
                    className="w-16 h-16 rounded-lg object-cover transition-all duration-300 group-active:scale-110 group-active:shadow-lg"
                    style={{ border: `2px solid ${colors.mainColor}20` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-active:bg-black/20 rounded-lg transition-all duration-300 opacity-0 group-active:opacity-100">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    {c.name}
                  </h3>
                  <div className="flex flex-col gap-1 mt-1 text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    <div className="flex gap-2">
                      <span>{isArabic ? "المكانة:" : "Position:"} {c.position === "رئيسي" ? (isArabic ? "رئيسي" : "Main") : (isArabic ? "فرعي" : "Sub")}</span>
                      {c.position === "فرعي" && c.parent && (
                        <>
                          <span>•</span>
                          <span>{isArabic ? "القسم الرئيسي:" : "Parent:"} {c.parent}</span>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <span className="font-semibold" style={{ color: colors.gold }}>
                        {
                          c.position === "رئيسي"
                            ? getCategoryItems(c.id).length
                            : getSubCategoryItems(c.id, c.subCategoryId).length
                        }{isArabic ? "منتج" : "products"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb" }}>
                <span
                  className={`px-3 py-1 rounded-lg text-xs font-semibold ${c.active ? "text-green-400 bg-green-500/10" : "text-red-400 bg-red-500/10"
                    }`}
                >
                  {c.active ? (isArabic ? "نشط" : "Active") : isArabic ? "غير نشط" : "Inactive"}
                </span>
              </div>
              <div className="flex gap-2 pt-2 flex-nowrap overflow-x-auto">
                <button
                  onClick={async () => {
                    try {
                      const categoryData = await fetchCategoryById(c.id);
                      if (categoryData) {
                        const rawImage =
                          categoryData.imageUrl ||
                          categoryData.ImageUrl ||
                          categoryData.image ||
                          categoryData.Image ||
                          "";
                        setShowView({
                          ...c,
                          name: categoryData.categoryName || c.name,
                          description: categoryData.description || c.description,
                          imageUrl: rawImage ? buildImageUrl(rawImage) : c.imageUrl,
                        });
                      } else {
                        setShowView(c);
                      }
                    } catch (err) {
                      setShowView(c);
                    }
                  }}
                  className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all active:scale-105 shadow-sm active:shadow-md whitespace-nowrap"
                  style={{ background: colors.mainColor }}
                >
                  <FiEye className="inline mr-1" size={14} />
                  <span>{isArabic ? "عرض" : "View"}</span>
                </button>
                {perms.editDivisions && (
                  <button
                    onClick={async () => {
                      try {
                        const categoryData = await fetchCategoryById(c.id);
                        if (categoryData) {
                          const rawImage =
                            categoryData.imageUrl ||
                            categoryData.ImageUrl ||
                            categoryData.image ||
                            categoryData.Image ||
                            "";
                          setShowEdit({
                            ...c,
                            id: c.id,
                            subCategoryId: c.subCategoryId,
                            name: c.name,
                            description: c.description,
                            active: c.active,
                            parentCategoryId: c.parentCategoryId,
                            imageUrl: rawImage ? buildImageUrl(rawImage) : c.imageUrl,
                            imageFile: null,
                          });
                        } else {
                          setShowEdit(c);
                        }
                      } catch (err) {
                        setShowEdit(c);
                      }
                    }}
                    className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all active:scale-105 shadow-sm active:shadow-md whitespace-nowrap"
                    style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                  >
                    <FiEdit2 className="inline mr-1" size={14} />
                    <span>{isArabic ? "تعديل" : "Edit"}</span>
                  </button>
                )}
                {perms.deleteDivisions && (
                  <button
                    onClick={() => handleDelete(c)} className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all active:scale-105 shadow-sm active:shadow-md whitespace-nowrap"
                    style={{ background: colors.red }}
                  >
                    <FiTrash2 className="inline mr-1" size={14} />
                    <span>{isArabic ? "حذف" : "Delete"}</span>
                  </button>
                )}
              </div>
            </div>
          )}
        />
      </div>

      {/* ===== مودالات ===== */}
      <AnimatePresence>
        {showAdd && (
          <CategoryModal
            title={isArabic ? "إضافة قسم جديد" : "Add Category"}
            category={newCategory}
            setCategory={setNewCategory}
            categories={categories}
            onSave={handleAdd}
            onClose={() => setShowAdd(false)}
            isDark={isDark}
            colors={colors}
            isArabic={isArabic}
            loading={loading}
          />
        )}
        {showEdit && (
          <CategoryModal
            title={isArabic ? "تعديل القسم" : "Edit Category"}
            category={showEdit}
            setCategory={setShowEdit}
            categories={categories}
            onSave={handleEditSave}
            onClose={() => setShowEdit(null)}
            isDark={isDark}
            colors={colors}
            isArabic={isArabic}
            loading={loading}
          />
        )}
      </AnimatePresence>

      {/* ===== مودال العرض ===== */}
      {/* ===== مودال المنتجات ===== */}
      <AnimatePresence>
        {showProductsModal && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            style={{ zIndex: 10000 }}
          >
            <div
              className={`p-6 rounded-2xl w-[95%] max-w-2xl max-h-[80vh] overflow-y-auto shadow-lg ${isDark ? "bg-[#1E293B]" : "bg-white"
                }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold" style={{ color: colors.mainColor }}>
                  {isArabic ? "المنتجات التابعة" : "Category Products"}
                </h2>
                <button onClick={() => setShowProductsModal(false)}>
                  <FiX size={20} />
                </button>
              </div>

              {selectedProducts.length === 0 ? (
                <p className="text-center opacity-70">
                  {isArabic ? "لا يوجد منتجات" : "No products found"}
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedProducts.map((item) => {
                    const itemId = item.itemId || item.ItemId;
                    const itemName =
                      item.itemName || item.ItemName || item.name || "—";
                    const imageSrc = buildProductImageUrl(
                      item.mainImageUrl ||
                        item.MainImageUrl ||
                        item.mainImage ||
                        item.imageUrl ||
                        ""
                    );
                    const price = getProductPrice(item);
                    const stock = getProductStockQty(item);
                    const variants = item.variants || item.Variants || [];

                    return (
                      <div
                        key={itemId}
                        className="mb-1 p-3 rounded-lg border flex gap-3"
                        style={{ borderColor: isDark ? "#334155" : "#e5e7eb" }}
                      >
                        <img
                          src={imageSrc}
                          alt={itemName}
                          className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                          style={{ border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}` }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://placehold.co/80x80/e0e0e0/666?text=Product";
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <h6 className="font-bold truncate">{itemName}</h6>
                          <div className="text-sm flex flex-wrap gap-x-4 gap-y-1 opacity-90">
                            <span>
                              {isArabic ? "السعر:" : "Price:"} {price}
                            </span>
                            <span>
                              {isArabic ? "المخزون:" : "Stock:"} {stock}
                            </span>
                            <span>
                              {isArabic ? "المتغيرات:" : "Variants:"}{" "}
                              {Array.isArray(variants) ? variants.length : 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showView && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            style={{ zIndex: 9999 }}
          >
            <div
              className={`p-6 rounded-2xl w-[90%] max-w-sm shadow-lg ${isDark ? "bg-[#1E293B]" : "bg-white"
                }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold" style={{ color: colors.mainColor }}>
                  {isArabic ? "معاينة القسم" : "View Category"}
                </h2>
                <button
                  className="text-gray-400 active:text-red-500"
                  onClick={() => setShowView(null)}
                >
                  <FiX size={20} />
                </button>
              </div>

              <div className="text-center">
                <div
                  className="relative w-32 h-32 mx-auto mb-3 cursor-pointer group"
                  onClick={() => setZoomedImage(showView.imageUrl)}
                >
                  <img
                    src={showView.imageUrl}
                    alt={showView.name}
                    className="w-full h-full rounded-lg object-cover transition-all duration-300 group-active:scale-110 group-active:shadow-2xl"
                    style={{ border: `2px solid ${colors.mainColor}20` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-active:bg-black/20 rounded-lg transition-all duration-300">
                    <div className="opacity-0 group-active:opacity-100 transition-opacity duration-300">
                      <svg
                        className="w-8 h-8 text-white drop-shadow-lg"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-1">{showView.name}</h3>
                <p className="text-sm opacity-80 mb-1">
                  {isArabic ? "المكان:" : "Position:"} {showView.position}
                </p>
                <p className="text-sm opacity-80 mb-1">
                  {isArabic ? "الحالة:" : "Status:"}{" "}
                  <span
                    className={
                      showView.active ? "text-green-500" : "text-red-500"
                    }
                  >
                    {showView.active
                      ? isArabic
                        ? "نشط"
                        : "Active"
                      : isArabic
                        ? "غير نشط"
                        : "Inactive"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* ===== مودال تكبير الصورة ===== */}
      <AnimatePresence>
        {zoomedImage && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/90 backdrop-blur-md"
            style={{ zIndex: 12000 }}
            onClick={() => setZoomedImage(null)}
          >
            {/* زر الإغلاق */}
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 z-10 p-3 rounded-full bg-white/10 active:bg-white/20 backdrop-blur-sm transition-all duration-300 active:scale-110"
              style={{ color: "#fff" }}
              aria-label={isArabic ? "إغلاق" : "Close"}
            >
              <FiX size={24} />
            </button>

            {/* حاوية الصورة */}
            <div
              className="relative max-w-[95%] max-h-[95vh] p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={zoomedImage}
                alt="Zoomed"
                className="max-w-full max-h-[90vh] rounded-xl shadow-2xl object-contain"
              />
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* CSS Animations */}
      <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes zoomIn {
            from {
              transform: scale(0.8);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes fadeInUp {
            from {
              transform: translate(-50%, 20px);
              opacity: 0;
            }
            to {
              transform: translate(-50%, 0);
              opacity: 1;
            }
          }
        `}</style>
    </PageContainer>
  );
}

/* ============ مودال الإضافة / التعديل ============ */
function CategoryModal({
  isArabic,
  isDark,
  colors,
  title,
  category,
  setCategory,
  categories,
  onSave,
  onClose,
  loading,
}) {
  const { colors: uiColors } = useUI();
  const finalColors = colors || uiColors;
  const fileInputId = title.includes("إضافة") ? "uploadImageAdd" : "uploadImageEdit";

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: "warning",
        title: isArabic ? "نوع ملف غير صحيح" : "Invalid File Type",
        text: isArabic ? "يرجى اختيار ملف صورة" : "Please select an image file",
        confirmButtonColor: finalColors.mainColor,
        ...configureSwalTop(),
      });
      const fileInputId = title.includes("إضافة") ? "uploadImageAdd" : "uploadImageEdit";

      return;
    }

    const url = URL.createObjectURL(file);
    setCategory({ ...category, imageUrl: url, imageFile: file });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: "warning",
        title: isArabic ? "نوع ملف غير صحيح" : "Invalid File Type",
        text: isArabic ? "يرجى اختيار ملف صورة" : "Please select an image file",
        confirmButtonColor: finalColors.mainColor,
        ...configureSwalTop(),
      });
      return;
    }

    const url = URL.createObjectURL(file);
    setCategory({ ...category, imageUrl: url, imageFile: file });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 10000 }} >
      <div
        className={`relative w-[95%] sm:w-[90%] max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl p-6 ${isDark ? "bg-[#1E293B] text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold" style={{ color: finalColors.mainColor }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 active:text-red-500 transition"
          >
            <FiX size={22} />
          </button>
        </div>

        {/* الاسم */}
        <label className="block mb-2 font-semibold">
          {isArabic ? "اسم القسم" : "Category Name"}
        </label>
        <input
          type="text"
          value={category.name}
          onChange={(e) => setCategory({ ...category, name: e.target.value })}
          className="w-full rounded-lg border border-gray-600/40 px-3 py-2 mb-4 bg-transparent focus:outline-none"
        />

        {/* الوصف */}
        <label className="block mb-2 font-semibold">
          {isArabic ? "الوصف" : "Description"}
        </label>
        <textarea
          value={category.description || ""}
          onChange={(e) => setCategory({ ...category, description: e.target.value })}
          className="w-full rounded-lg border border-gray-600/40 px-3 py-2 mb-4 bg-transparent focus:outline-none"
          rows={3}
          placeholder={isArabic ? "أدخل وصف القسم " : "Enter category description "}
        />

        {/* الصورة */}
        <label className="block mb-3 font-semibold">
          {isArabic ? "صورة القسم" : "Category Image"}
        </label>

        {/* Preview Area with Drag & Drop */}
        <div
          className="relative w-full rounded-xl overflow-hidden mb-3 transition-all duration-200 border-2 border-dashed cursor-pointer"
          style={{
            borderColor: category.imageUrl ? (isDark ? "rgba(116,0,191,0.3)" : "rgba(116,0,191,0.2)") : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
            backgroundColor: category.imageUrl ? (isDark ? "rgba(116,0,191,0.05)" : "rgba(116,0,191,0.02)") : (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"),
            minHeight: "200px",
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = finalColors.mainColor;
            e.currentTarget.style.backgroundColor = isDark ? "rgba(116,0,191,0.1)" : "rgba(116,0,191,0.05)";
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = category.imageUrl ? (isDark ? "rgba(116,0,191,0.3)" : "rgba(116,0,191,0.2)") : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)");
            e.currentTarget.style.backgroundColor = category.imageUrl ? (isDark ? "rgba(116,0,191,0.05)" : "rgba(116,0,191,0.02)") : (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)");
          }}
          onClick={() => {
            if (!category.imageUrl) {
              document.getElementById(fileInputId).click();
            }
          }}
        >
          {category.imageUrl ? (
            <>
              <div className="relative w-full h-full flex items-center justify-center p-4" style={{ minHeight: "200px" }}>
                <img
                  src={category.imageUrl}
                  alt="preview"
                  className="max-w-full max-h-[300px] object-contain rounded-lg"
                  style={{
                    width: "auto",
                    height: "auto",
                    maxWidth: "100%",
                    maxHeight: "300px",
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://placehold.co/400x400.png?text=No+Image";
                  }}
                />

                {/* زر تعديل الصورة أعلى الصورة */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    document.getElementById(fileInputId)?.click();
                  }}
                  className="absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] sm:text-xs font-bold shadow-lg transition-all duration-200 active:scale-[0.97] backdrop-blur-md"
                  style={{
                    background: `linear-gradient(135deg, ${finalColors.mainColor}f2, ${finalColors.mainColor}d9)`,
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.22)",
                    boxShadow: `0 8px 20px ${finalColors.mainColor}40`,
                  }}
                  title={isArabic ? "تعديل الصورة" : "Edit Image"}
                >
                  <FiEdit2 size={13} />
                  <span>{isArabic ? "تعديل الصورة" : "Edit Image"}</span>
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center p-8" style={{ minHeight: "200px" }}>
              <div className="mb-4">
                <FiUploadCloud
                  className="text-5xl mx-auto"
                  style={{ color: finalColors.mainColor, opacity: 0.6 }}
                />
              </div>
              <p className="text-sm font-medium mb-2" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                {isArabic ? "اضغط أو اسحب الصورة هنا" : "Click or drag image here"}
              </p>
              <p className="text-xs mb-4" style={{ color: isDark ? "#64748b" : "#9ca3af" }}>
                {isArabic ? "PNG, JPG, WEBP حتى 10MB" : "PNG, JPG, WEBP up to 10MB"}
              </p>
              <button
                onClick={() => document.getElementById(fileInputId)?.click()}
                className="px-6 py-2.5 rounded-lg text-white font-medium shadow-md active:shadow-lg transform active:scale-105 transition-all duration-200"
                style={{ background: colors.mainColor }}
              >
                {isArabic ? "اختر صورة" : "Choose Image"}
              </button>
            </div>
          )}
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          id={title.includes("إضافة") ? "uploadImageAdd" : "uploadImageEdit"}
          style={{
            display: "none",
            visibility: "hidden",
            position: "absolute",
            width: 0,
            height: 0,
            opacity: 0,
            pointerEvents: "none"
          }}
        />

        {/* Remove image button if image exists */}
        {category.imageUrl && (
          <button
            onClick={(e) => {
              e.preventDefault();
              setCategory({ ...category, imageUrl: "", imageFile: null });
              // Reset file input
              const fileInput = document.getElementById(fileInputId);
              if (fileInput) fileInput.value = "";
            }}
            className="w-full px-4 py-2 rounded-lg text-white text-sm font-medium transition-all duration-200 active:scale-[1.02]"
            style={{
              background: "#EF4444",
              marginBottom: "1rem"
            }}
          >
            {isArabic ? "إزالة الصورة" : "Remove Image"}
          </button>
        )}

        {/* الحالة */}
        <div className="mb-4" dir={isArabic ? "rtl" : "ltr"}>
          <label className="block mb-3 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827", textAlign: isArabic ? "right" : "left" }}>
            {isArabic ? "الحالة" : "Activation Status"}
          </label>
          <div
            className="flex items-center"
            style={{
              justifyContent: "flex-start",
              direction: isArabic ? "rtl" : "ltr"
            }}
          >
            <label
              className="flex items-center gap-3 cursor-pointer group"
              style={{
                flexDirection: isArabic ? "row" : "row",
                direction: isArabic ? "rtl" : "ltr"
              }}
            >
              {/* Toggle Switch - Same design as EmployeesTab */}
              <button
                onClick={() => setCategory({ ...category, active: !category.active })}
                className="relative w-12 h-6 rounded-full transition-colors cursor-pointer"
                style={{
                  backgroundColor: category.active ? finalColors.mainColor : (isDark ? "#475569" : "#d1d5db")
                }}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${category.active ? "translate-x-6" : "translate-x-0"
                    }`}
                />
              </button>

              {/* Status Text with Badge Style */}
              <div
                className={`
                  px-4 py-1.5 rounded-lg font-semibold text-sm transition-all duration-300
                  ${category.active
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border border-gray-500/20'
                  }
                  group-active:scale-105
                `}
                style={{
                  backgroundColor: category.active
                    ? 'rgba(16, 185, 129, 0.1)'
                    : (isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(107, 114, 128, 0.1)'),
                  color: category.active
                    ? '#059669'
                    : (isDark ? '#94a3b8' : '#4b5563'),
                  borderColor: category.active
                    ? 'rgba(16, 185, 129, 0.2)'
                    : (isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(107, 114, 128, 0.2)'),
                }}
              >
                {category.active
                  ? (isArabic ? "✓ نشط" : "✓ Active")
                  : (isArabic ? "✗ غير نشط" : "✗ Inactive")
                }
              </div>
            </label>
          </div>
        </div>

        {/* الموقع */}
        <label className="block mb-2 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
          {isArabic ? "مكان القسم" : "Category Position"}
        </label>
        <select
          value={category.position}
          disabled={!!category.id}
          onChange={(e) =>
            setCategory({ ...category, position: e.target.value, parent: "" })
          }
          className="w-full rounded-lg border px-3 py-2 mb-2 opacity-80 cursor-not-allowed"
        >
          <option value="رئيسي">رئيسي</option>
          <option value="فرعي">فرعي</option>
        </select>

        {category.id && (
          <p className="text-xs text-red-400 mb-3">
            لا يمكن تحويل القسم من رئيسي إلى فرعي أو العكس
          </p>
        )}
        {/* القسم الرئيسي لو فرعي */}
        {category.position === "فرعي" && !category.id && (
          <div className="mb-4">
            <label className="block mb-2 font-semibold">
              {isArabic ? "القسم الرئيسي" : "Main Category"}
            </label>

            <select
              value={category.parentCategoryId || ""}
              onChange={(e) =>
                setCategory({
                  ...category,
                  parentCategoryId: parseInt(e.target.value),
                  parent: categories.find(c => c.id === parseInt(e.target.value))?.name || ""
                })
              }
              className="w-full rounded-lg border px-3 py-2"
            >
              <option value="">
                {isArabic ? "اختر قسم رئيسي" : "Select main category"}
              </option>
              {categories
                .filter(c => c.position === "رئيسي")
                .map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
            </select>
          </div>
        )}



        <button
          onClick={onSave}
          disabled={loading}
          className={`w-full py-2 mt-2 rounded-lg text-white font-semibold ${loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          style={{ background: colors.mainColor }}
        >
          {loading ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
        </button>

      </div>
    </div>
  );
}
